import { Link } from 'react-router-dom'
import { HelpCircle } from 'lucide-react'

import { useCatalogStats } from '../../hooks/useCatalogStats'
import AnimatedCompass from '../ui/AnimatedCompass'
import { MagneticWrapper, GridBackground, InfiniteMarquee } from '../ui'
import buttonClasses from '../ui/buttonClasses'

// Static fallback covers the ~100ms before /api/v1/stats responds — kept close to the live count so the page never reads as broken.
const FALLBACK_TOOL_COUNT = 400

// Every tool we have an affiliate partnership with (app/affiliates.py), mixed
// with a few household names so the strip still reads as "the AI landscape".
// When a new program approves, add its tool here too.
const MARQUEE_LOGOS = [
  { name: 'ChatGPT', domain: 'openai.com', slug: 'chatgpt' },
  { name: 'ElevenLabs', domain: 'elevenlabs.io', slug: 'elevenlabs' },
  { name: 'Jenni AI', domain: 'jenni.ai', slug: 'jenni-ai' },
  { name: 'Synthesia', domain: 'synthesia.io', slug: 'synthesia' },
  { name: 'Claude', domain: 'anthropic.com', slug: 'claude' },
  { name: 'SciSpace', domain: 'scispace.com', slug: 'scispace' },
  { name: 'Sudowrite', domain: 'sudowrite.com', slug: 'sudowrite' },
  { name: 'Cal.com', domain: 'cal.com', slug: 'cal-com' },
  { name: 'Gemini', domain: 'gemini.google.com', slug: 'gemini' },
  { name: 'Taskade', domain: 'taskade.com', slug: 'taskade' },
  { name: 'Paperpal', domain: 'paperpal.com', slug: 'paperpal' },
  { name: 'Postiz', domain: 'postiz.com', slug: 'postiz' },
  { name: 'Perplexity', domain: 'perplexity.ai', slug: 'perplexity-ai' },
  { name: 'beehiiv', domain: 'beehiiv.com', slug: 'beehiiv' },
  { name: 'Circleback', domain: 'circleback.ai', slug: 'circleback' },
  { name: 'Superlist', domain: 'superlist.com', slug: 'superlist' },
  { name: 'Cursor', domain: 'cursor.com', slug: 'cursor' },
  { name: 'screenpipe', domain: 'screenpi.pe', slug: 'screenpipe' },
  { name: 'VoiceOS', domain: 'voiceos.com', slug: 'voiceos' },
  { name: 'Viktor', domain: 'viktor.com', slug: 'viktor-com' },
  { name: 'Notion', domain: 'notion.so', slug: 'notion' },
  { name: 'Runable', domain: 'runable.com', slug: 'runable' },
  { name: 'TesterArmy', domain: 'tester.army', slug: 'testerarmy' }
]

export default function Hero() {
  const { roundedToolsText } = useCatalogStats() // {/* Dynamic — do not hardcode */}
  
  const handleStartTour = () => {
    window.dispatchEvent(new CustomEvent('ai-compass-start-tour'))
  }

  return (
    <GridBackground>
      <header className="relative pt-9 pb-8 md:pt-20 md:pb-16">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-5 md:grid-cols-[minmax(0,1fr)_auto] md:gap-16 relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-line/40 bg-bg-elev/40 backdrop-blur-sm shadow-sm px-2.5 py-1 text-xs font-medium text-muted">
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full bg-accent"
                style={{ boxShadow: '0 0 0 3px color-mix(in oklab, var(--accent) 25%, transparent)' }}
              />
              For undergraduates · {roundedToolsText} tools curated {/* Dynamic — do not hardcode */}
            </div>

            {/* The rotating audience word ("For Students. / Coders. / ...")
                rendered blank mid-cycle, so the H1 often read "For" and nothing
                else. A fixed headline says the same thing every time. */}
            <h1 className="t-h1 mt-4 mb-3.5 md:max-w-[17ch]">
              AI tools for students, <span className="text-muted">checked by a person.</span>
            </h1>

            <p className="mb-5 max-w-[38ch] text-pretty text-[15px] text-muted md:max-w-[50ch] md:text-lg">
              {roundedToolsText} tools, each with its real free-tier limits written out, so you know
              what you get before you sign up. Answer three questions and get a shortlist. {/* Dynamic — do not hardcode */}
            </p>

            <div className="flex flex-wrap items-center gap-2.5">
              <MagneticWrapper strength={0.25}>
                <Link
                  to="/ai-tool-finder"
                  className={buttonClasses({ variant: 'dark', size: 'lg', className: 'group' })}
                >
                  Find my tools
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  >
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </Link>
              </MagneticWrapper>
              <Link
                to="/tools"
                className={buttonClasses({ variant: 'secondary', size: 'lg' })}
              >
                Browse the catalogue
              </Link>
              {/* Third tier on purpose. The tour is a helper, not a peer of
                  "Start the wizard" and "Browse the catalog" — as a bordered
                  button it made three near-identical controls and flattened
                  the choice. Text-link treatment keeps it available without
                  competing for the primary click. */}
              <button
                type="button"
                onClick={handleStartTour}
                className="inline-flex items-center gap-1.5 rounded-full px-2 py-3 text-sm font-medium text-muted underline-offset-4 transition-colors hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                <HelpCircle className="h-4 w-4" /> Take a Tour
              </button>
            </div>

            <div
              className="mt-6 flex flex-wrap gap-[18px] text-[13px] text-muted"
              aria-label="Quick facts"
            >
              <span className="inline-flex items-center gap-1.5">
                <b className="font-semibold text-ink">Free</b> · to use
              </span>
              <span className="inline-flex items-center gap-1.5">
                <b className="font-semibold text-ink">No account</b> · needed
              </span>
              {/* "4,000+ users" sat next to a live members count in the hundreds
                  on /about. Only claims the site can show belong here. */}
              <span className="inline-flex items-center gap-1.5 border-l border-line pl-4">
                Paid placements are always labelled
              </span>
            </div>

            <div
              aria-hidden="true"
              className="mt-7 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-2 md:hidden"
            >
              <span>Scroll</span>
              <svg
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3 w-3"
              >
                <path d="M3 5l3 3 3-3" />
              </svg>
            </div>
          </div>

          <div className="hidden justify-center md:flex">
            <AnimatedCompass size={340} />
          </div>
        </div>

        {/* Horizontal Looping Marquee */}
        <div className="mt-20 md:mt-24 w-full relative z-10 hidden md:block">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-muted-2 mb-5">
            Compare {roundedToolsText} AI tools {/* Dynamic — do not hardcode */}
          </p>
          <div className="py-3 min-h-[80px]">
            <InfiniteMarquee
              items={MARQUEE_LOGOS}
              speed={40}
              gap="1rem"
              renderItem={(logo, index) => (
                <Link
                  key={`${logo.name}-${index}`}
                  to={`/tools/${logo.slug}`}
                  className="flex items-center gap-3 rounded-2xl border border-line/50 bg-bg-elev/40 backdrop-blur-md px-5 py-3 shadow-lg hover:border-accent/50 hover:bg-bg-elev/60 transition-all duration-300 transform hover:scale-[1.05] cursor-pointer"
                >
                  <img
                    src={`/icon/${logo.domain}`}
                    alt={logo.name}
                    className="h-7 w-7 rounded-lg p-0.5 object-contain shadow-[0_1px_4px_rgba(0,0,0,0.10)] border border-line/30"
                    style={{ backgroundColor: 'var(--logo-bg)' }}
                    loading="lazy"
                  />
                  <span className="font-semibold text-ink-2">{logo.name}</span>
                </Link>
              )}
            />
          </div>
        </div>
      </header>
    </GridBackground>
  )
}
